import {
  Anchor,
  Button,
  Container,
  HoverCard,
  LoadingOverlay,
  Modal,
  NumberInput,
  ScrollArea,
  TextInput,
} from "@mantine/core";
import { useDisclosure, useListState } from "@mantine/hooks";
import {
  IconDeviceFloppy,
  IconQuestionMark,
  IconRestore,
  IconSelector,
  IconTrash,
} from "@tabler/icons-react";
import {
  memo,
  Reducer,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import { inAppPaths } from "src/App";
import DeviceDropDown from "src/pages/components/DeviceDropDown";
import Popup from "src/pages/components/Popup";
import TimePicker from "src/pages/components/TimePicker";
import { getI18t } from "src/pages/i18n/i18n";
import TimeWindowGroupSelection from "src/pages/in-app/time-window-group-list/TimeWindowGroupSelection";
import useIsMobile from "src/pages/utils/isMobileHook";
import { createSuccessResponse } from "src/repositories/model/FailableResponse";
import deviceService from "src/services/core/device/Device.service";
import Device from "src/services/core/device/models/Device";
import groupService from "src/services/core/group/Group.service";
import { timeWindowGroupsService } from "src/services/core/time-window-group/TimeWindowGroups.service";
import { reduxStore, useReduxAppSelector } from "src/services/redux/ReduxStore";
import { delay, isArrayEqual } from "src/utils/Utils";
import {
  nullToastUpdateOptions,
  toastCommonError,
  toastDeviceResponseReport,
} from "../../utils/ToastUtils";

// map from id on device error code to display message in i18n
const idOnDeviceErrorCodeMap = {
  "id-used": "idOnDeviceUniqueRequired" as const,
  "out-of-range": "idOnDeviceInRangeRequired" as const,
};

const iconButtonClass = "!size-8 !p-0 flex justify-center items-center m-[2px]";
const iconSize = 23;

const dayOfWeekArray = [
  "monday" as const,
  "tuesday" as const,
  "wednesday" as const,
  "thursday" as const,
  "friday" as const,
  "saturday" as const,
  "sunday" as const,
];

const TimeWindowGroupList: () => JSX.Element = () => {
  const [isLoading, { close: turnOffLoading, open: turnOnLoading }] =
    useDisclosure(true);
  const [
    selectModalOpened,
    { open: openSelectModal, close: closeSelectModal },
  ] = useDisclosure(false);
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);
  const [noSelectedGroup, { open: setNoSelectedGroupTrue }] =
    useDisclosure(false);
  const [affectedSns, affectedSnsHandler] = useListState<string>([]);
  const [devices, setDevices] = useState<Device[]>();
  const timeWindowGroupList = useReduxAppSelector(
    (a) => a.timeWindowGroupSlice
  );
  const [currentTimeWindowGroup, _setCurrentTimeWindowGroup] = useState<
    TimeWindowGroup | undefined
  >();
  const [idOnDeviceErrorCode, setIdOnDeviceErrorCode] =
    useState<keyof typeof idOnDeviceErrorCodeMap>();
  const { t: realT } = useTranslation(undefined, {
    keyPrefix: "inApp.timeWindowGroupList",
  });
  const t = getI18t(realT);
  // const { t } = useTranslation(undefined, {
  //   keyPrefix: "inApp.timeWindowGroupList",
  // });
  const [isMobile] = useIsMobile();
  const [idOnDevice, setIdOnDevice] = useState<number>(0);
  const [timeWindowGroupName, setTimeWindowGroupName] = useState<string>("");
  const groupId = useRef<number>();
  const initialTimeWindows = useRef<(TimeWindowShort | undefined)[]>();
  const [modifiedTimeWindows, modifiedTimeWindowsHandler] = useListState<
    TimeWindowShort | undefined
  >();
  const copiedColumn = useRef<Array<TimeWindowShort | undefined> | undefined>();

  const [isTimeWindowGroupModified, isTimeWindowGroupModifiedDispatch] =
    useReducer<
      Reducer<
        // isModified: whether any of the fields is modified, diferenceSet: the set of modifications
        // This Reducer aims to only change the wrapper state object when the isModified state is changed.
        // The differenceSet state only exists because an internal variable mechanism can't be found in useReducer.
        { isModified: boolean; differenceSet: Set<string> },
        | { action: "reset" }
        | ({ action: "change" | "change-back" } & (
            | { target: "name" | "idOnDevice" }
            | { target: "timeWindow"; i: number }
          ))
      >
    >(
      (state, actionObject) => {
        if (actionObject.action == "reset") {
          return { isModified: false, differenceSet: new Set() };
        }
        if (actionObject.action == "change") {
          state.differenceSet.add(
            actionObject.target == "timeWindow"
              ? `timeWindow-${actionObject.i}`
              : actionObject.target
          );
          return state.isModified ? state : { ...state, isModified: true };
        }
        state.differenceSet.delete(
          actionObject.target == "timeWindow"
            ? `timeWindow-${actionObject.i}`
            : actionObject.target
        );
        return state.isModified && state.differenceSet.size == 0
          ? { ...state, isModified: false }
          : state;
      },
      { isModified: false, differenceSet: new Set() }
    );

  useEffect(() => {
    (async () => {
      const delayPromise = delay(300);
      const groupResult = await groupService.getSelectedGroup();
      if (!groupResult.success) {
        toastCommonError(groupResult.code);
        return;
      }
      if (groupResult.value == null) {
        setNoSelectedGroupTrue();
        return;
      }

      groupId.current = groupResult.value.id;

      const twgsPromise = timeWindowGroupsService.get(
        groupResult.value.id,
        undefined,
        undefined,
        undefined,
        false
      );
      const twgsResult = await twgsPromise;
      const reduxDeviceList = reduxStore.getState().deviceList;
      const deviceResult =
        reduxDeviceList != null
          ? createSuccessResponse(reduxDeviceList)
          : await deviceService.getDevices(groupResult.value.id);

      await delayPromise;
      turnOffLoading();

      if (!twgsResult.success) {
        toastCommonError(twgsResult.code);
        return;
      }
      if (!deviceResult.success) {
        toastCommonError(deviceResult.code);
        return;
      }

      const sns = deviceResult.value.map((d) => d.serialNumber);
      affectedSnsHandler.setState(sns);
      setDevices(deviceResult.value);
      setTimeWindowGroupExtra(twgsResult.value[0] ?? undefined);
    })();
  }, []);

  const reset = useCallback(
    (timeWindowGroup?: TimeWindowGroup) => {
      const twg = timeWindowGroup ?? currentTimeWindowGroup;
      if (twg == null) {
        return;
      }
      setIdOnDevice(twg.idOnDevice);
      setTimeWindowGroupName(twg.name);
      modifiedTimeWindowsHandler.setState(
        timeWindowGroup != null
          ? toTimeWindowShortArray(twg.timeWindows)
          : [...(initialTimeWindows.current ?? [])]
      );
      isTimeWindowGroupModifiedDispatch({ action: "reset" });
    },
    [currentTimeWindowGroup]
  );

  const setTimeWindowGroupExtra = useCallback(
    (twg: TimeWindowGroup | undefined) => {
      _setCurrentTimeWindowGroup(twg);
      initialTimeWindows.current =
        twg != null ? toTimeWindowShortArray(twg.timeWindows) : undefined;
      reset(twg);
    },
    [reset]
  );

  const validateIdOnDevice = useCallback(
    (id: number) => {
      if (timeWindowGroupList == null || currentTimeWindowGroup == null) {
        return;
      }
      if (id < 1 || id > 64) {
        setIdOnDeviceErrorCode("out-of-range");
      }
      if (
        timeWindowGroupList.find(
          (twg) => twg.id != currentTimeWindowGroup?.id && twg.idOnDevice == id
        ) != null
      ) {
        setIdOnDeviceErrorCode("id-used");
      }
    },
    [timeWindowGroupList, currentTimeWindowGroup]
  );

  const deleteCurrent = useCallback(async () => {
    if (currentTimeWindowGroup == null || currentTimeWindowGroup.id == 0) {
      return;
    }

    const toastId = toast.loading(t("deleting"), {
      closeButton: true,
      closeOnClick: true,
    });
    const [result, _] = await Promise.all([
      timeWindowGroupsService.delete_(currentTimeWindowGroup.id, affectedSns),
      delay(200),
    ]);
    if (!result.success) {
      toastCommonError(result.code, toastId);
      return;
    }

    toastDeviceResponseReport(devices ?? [], result.value.report ?? null);
    toast.update(toastId, {
      ...nullToastUpdateOptions,
      render: t("deleteSuccess"),
      type: "success",
    });
    setTimeWindowGroupExtra(
      timeWindowGroupList?.filter(
        (twg) => twg.id != currentTimeWindowGroup.id
      )?.[0] ?? undefined
    );
    closeDeleteModal();
    turnOnLoading();
    await delay(200);
    turnOffLoading();
  }, [currentTimeWindowGroup, affectedSns]);

  const saveCurrent = useCallback(async () => {
    if (
      currentTimeWindowGroup == null ||
      timeWindowGroupList == null ||
      groupId.current == null ||
      initialTimeWindows.current == null ||
      modifiedTimeWindows == null
    ) {
      return;
    }

    if (idOnDeviceErrorCode != null) {
      return;
    }

    const toastId = toast.loading(t("saving"), {
      closeButton: true,
      closeOnClick: true,
    });
    const timeWindows = isArrayEqual(
      initialTimeWindows.current,
      modifiedTimeWindows,
      areTimeWindowShortsEqual
    )
      ? undefined
      : modifiedTimeWindows.flatMap<TimeWindow>((tws, i) => {
          if (tws == undefined) {
            return [];
          }
          return {
            ...tws,
            dayOfWeek: Math.floor(i / 8),
            zoneNumber: i % 8,
          };
        });
    const result =
      currentTimeWindowGroup.id == 0
        ? await timeWindowGroupsService.create(
            {
              idOnDevice,
              groupId: groupId.current,
              name: timeWindowGroupName,
              timeWindows: timeWindows ?? [],
            },
            affectedSns
          )
        : await timeWindowGroupsService.patch(
            currentTimeWindowGroup.id,
            {
              idOnDevice:
                currentTimeWindowGroup.idOnDevice == idOnDevice
                  ? undefined
                  : idOnDevice,
              name:
                currentTimeWindowGroup.name == timeWindowGroupName
                  ? undefined
                  : timeWindowGroupName,
              timeWindows,
            },
            affectedSns
          );
    if (!result.success) {
      toastCommonError(result.code, toastId);
      return;
    }

    if (result.value.report != null) {
      toastDeviceResponseReport(devices ?? [], result.value.report);
    }
    toast.update(toastId, {
      ...nullToastUpdateOptions,
      render: t("saveSuccess"),
      type: "success",
    });
    setTimeWindowGroupExtra(
      result.value.value ?? {
        id: currentTimeWindowGroup.id,
        groupId: groupId.current,
        idOnDevice: idOnDevice,
        name: timeWindowGroupName,
        timeWindows: timeWindows ?? [],
      }
    );
    isTimeWindowGroupModifiedDispatch({ action: "reset" });
  }, [
    currentTimeWindowGroup,
    timeWindowGroupList,
    modifiedTimeWindows,
    affectedSns,
    timeWindowGroupName,
    idOnDevice,
  ]);

  const isModifiedTimeWindowsNull = modifiedTimeWindows == null;

  const onCellEdit = useCallback(
    (ctw: CellTimeWindow) => {
      if (modifiedTimeWindows == null) {
        return;
      }
      const i = ctw.dayOfWeek * 8 + ctw.zoneNumber;
      const value = ctw.value;
      modifiedTimeWindowsHandler.setItem(i, value);
      isTimeWindowGroupModifiedDispatch({
        action: areTimeWindowShortsEqual(value, initialTimeWindows.current?.[i])
          ? "change-back"
          : "change",
        target: "timeWindow",
        i,
      });
    },
    [isModifiedTimeWindowsNull]
  );

  const onTimeWindowGroupSelect = useCallback(
    (twg: TimeWindowGroup) => {
      setTimeWindowGroupExtra(twg);
      closeSelectModal();
    },
    [setTimeWindowGroupExtra]
  );

  const onAddTimeWindowGroup = useCallback(() => {
    closeSelectModal();
    if (timeWindowGroupList == null || groupId.current == null) {
      return;
    }

    let idOnDevice = 1;
    timeWindowGroupList.some((twg) => {
      if (twg.idOnDevice == idOnDevice) {
        idOnDevice++;
        return false;
      } else {
        return true;
      }
    });
    setTimeWindowGroupExtra({
      id: 0,
      name: "",
      idOnDevice: idOnDevice,
      groupId: groupId.current,
      timeWindows: [],
    });
  }, [timeWindowGroupList, setTimeWindowGroupExtra]);

  const copyColumn = useCallback(
    (col: number) => {
      copiedColumn.current = modifiedTimeWindows
        .slice(col * 8, col * 8 + 8)
        .map((twg) => (twg == undefined ? undefined : { ...twg }));
    },
    [copiedColumn, modifiedTimeWindows]
  );

  const pasteColumn = useCallback(
    (targetCol: number) => {
      copiedColumn.current?.forEach((twg, colI) => {
        const index = targetCol * 8 + colI;
        modifiedTimeWindowsHandler.setItem(targetCol * 8 + colI, twg);
        isTimeWindowGroupModifiedDispatch({
          action: areTimeWindowShortsEqual(
            initialTimeWindows.current?.[index],
            twg
          )
            ? "change-back"
            : "change",
          target: "timeWindow",
          i: index,
        });
      });
    },
    [copiedColumn, modifiedTimeWindowsHandler]
  );

  const deleteColumn = useCallback(
    (targetCol: number) => {
      for (let colI = 0; colI < 8; colI++) {
        const index = targetCol * 8 + colI;
        modifiedTimeWindowsHandler.setItem(targetCol * 8 + colI, undefined);
        isTimeWindowGroupModifiedDispatch({
          action: areTimeWindowShortsEqual(
            initialTimeWindows.current?.[index],
            undefined
          )
            ? "change-back"
            : "change",
          target: "timeWindow",
          i: index,
        });
      }
    },
    [copiedColumn, modifiedTimeWindowsHandler]
  );
  const columns = Array(7)
    .fill(undefined)
    .map((_, dayOfWeek) => (
      <div key={"col-" + dayOfWeek}>
        <div className="bg-gray-200 font-semibold flex justify-center items-center p-1">
          {t(dayOfWeekArray[dayOfWeek])}
        </div>
        {Array(8)
          .fill(undefined)
          .map((_, zoneNumber) => (
            <Cell
              key={"cell" + (dayOfWeek * 8 + zoneNumber)}
              zoneNumber={zoneNumber}
              dayOfWeek={dayOfWeek}
              timeWindow={modifiedTimeWindows[dayOfWeek * 8 + zoneNumber]}
              onEdit={onCellEdit}
            />
          ))}
        <div className="flex flex-wrap justify-center mt-1 space-x-2">
          <Anchor
            size="compact-sm"
            onClick={() => {
              copyColumn(dayOfWeek);
            }}
          >
            {t("copyColumn")}
          </Anchor>
          <Anchor
            c={"cyan"}
            size="compact-sm"
            onClick={() => {
              pasteColumn(dayOfWeek);
            }}
          >
            {t("pasteColumn")}
          </Anchor>
          <Anchor
            c={"red"}
            size="compact-sm"
            onClick={() => {
              deleteColumn(dayOfWeek);
            }}
          >
            {t("deleteColumn")}
          </Anchor>
        </div>
      </div>
    ));

  return noSelectedGroup ? (
    <Navigate to={inAppPaths.groups} />
  ) : (
    <ScrollArea
      className="w-full h-full flex-grow-0 flex-shrink-0"
      scrollbars="y"
      classNames={{ viewport: "[&>div:first-child]:!block" }}
    >
      <Container
        size="xl"
        className="overflow-visible max-w-full"
        classNames={{ root: "max-w-full" }}
      >
        {isLoading ? (
          <div className="h-svh w-full">
            <LoadingOverlay
              className=""
              visible={true}
              zIndex={1000}
              // overlayProps={{ radius: "sm", blur: 2 }}
            />
          </div>
        ) : (
          <>
            {timeWindowGroupList == null ? (
              <div className="flex justify-center mt-10 text-gray-500">
                {t("getTimeWindowGroupsFailed")}
              </div>
            ) : (
              <>
                <div className="my-5 flex flex-col md:flex-row-reverse items-start md:justify-between w-full overflow-visible">
                  <div className="flex flex-col items-start md:items-end space-y-2">
                    <Button
                      color="green"
                      leftSection={<IconSelector size={20} />}
                      className="w-60"
                      onClick={openSelectModal}
                    >
                      {t("selectTimeWindowGroup")}
                    </Button>
                    <DeviceDropDown
                      className="w-60"
                      selected={affectedSns}
                      setSelected={affectedSnsHandler.setState}
                      deviceList={devices ?? []}
                      displayCount
                      comboboxProps={{}}
                    />
                    <div className="flex flex-row">
                      {currentTimeWindowGroup != null && (
                        <>
                          <Button
                            disabled={
                              !isTimeWindowGroupModified.isModified &&
                              currentTimeWindowGroup.id != 0
                            }
                            aria-label={t("saveButton")}
                            title={t("saveButton")}
                            variant="light"
                            color="blue"
                            onClick={saveCurrent}
                            className={
                              "!h-8 !pl-1 !pr-[0.35rem] flex justify-center items-center m-[2px]"
                            }
                            leftSection={<IconDeviceFloppy size={iconSize} />}
                            classNames={{
                              section: "!me-[0.3rem]",
                            }}
                          >
                            {t("saveButtonTitle")}
                          </Button>
                          <Button
                            disabled={!isTimeWindowGroupModified.isModified}
                            variant="light"
                            color="blue"
                            aria-label={t("restoreButton")}
                            title={t("restoreButton")}
                            onClick={() => reset()}
                            className={iconButtonClass}
                          >
                            <IconRestore size={iconSize} />
                          </Button>
                          <Button
                            disabled={currentTimeWindowGroup.id == 0}
                            variant="light"
                            color="red"
                            aria-label={t("deleteButton")}
                            title={t("deleteButton")}
                            onClick={openDeleteModal}
                            className={iconButtonClass}
                          >
                            <IconTrash size={iconSize} />
                          </Button>
                        </>
                      )}
                      {/* TODO: make hovercard accessible */}
                      <HoverCard shadow="md">
                        <HoverCard.Target>
                          <div
                            className={
                              iconButtonClass +
                              " bg-[var(--mantine-color-blue-light)] rounded-[var(--mantine-radius-default)] focus"
                            }
                          >
                            <IconQuestionMark
                              size={iconSize + 4}
                              color="var(--mantine-color-blue-filled)"
                            />
                          </div>
                        </HoverCard.Target>
                        <HoverCard.Dropdown className="!w-[min(45rem,100vw)]">
                          <div className="mt-3 ">
                            {t("explanation1")}
                            <br />- <b>{t("explanation2")}</b>
                            {t("explanation3")}
                            <br />- {t("explanation4")}
                            <br />- {t("explanation5")}
                          </div>
                        </HoverCard.Dropdown>
                      </HoverCard>
                    </div>
                  </div>
                  {currentTimeWindowGroup != null && (
                    <div>
                      <NumberInput
                        label={t("idOnDeviceLabel")}
                        value={idOnDevice}
                        onValueChange={(value) => {
                          setIdOnDevice(value.floatValue ?? 0);
                        }}
                        onBlur={(e) => {
                          const newIdOnDevie = parseInt(e.target.value);
                          isTimeWindowGroupModifiedDispatch({
                            action:
                              newIdOnDevie != currentTimeWindowGroup.idOnDevice
                                ? "change"
                                : "change-back",
                            target: "idOnDevice",
                          });
                          validateIdOnDevice(newIdOnDevie);
                        }}
                        onFocus={() => setIdOnDeviceErrorCode(undefined)}
                        error={
                          idOnDeviceErrorCode == null
                            ? undefined
                            : t(idOnDeviceErrorCodeMap[idOnDeviceErrorCode])
                        }
                        readOnly={currentTimeWindowGroup.id != 0}
                        variant={
                          currentTimeWindowGroup.id == 0 ? "default" : "filled"
                        }
                      />
                      <TextInput
                        label={t("nameLabel")}
                        onChange={(e) => {
                          isTimeWindowGroupModifiedDispatch({
                            action:
                              e.target.value.trim() !=
                              currentTimeWindowGroup.name
                                ? "change"
                                : "change-back",
                            target: "name",
                          });
                          setTimeWindowGroupName(e.target.value);
                        }}
                        value={timeWindowGroupName}
                      />
                    </div>
                  )}
                </div>
                {currentTimeWindowGroup == null ? (
                  <div className="flex justify-center mt-10 text-gray-500">
                    <span>
                      {t("noTimeWindowGroups1")}
                      <b>{t("selectTimeWindowGroup")}</b>
                      {t("noTimeWindowGroups2")}
                      <b> {t("noTimeWindowGroupsOr")} </b>
                      {t("noTimeWindowGroups3")}
                      <Anchor>
                        <Link to={inAppPaths.devices}>
                          {t("noTimeWindowGroupsDeviceLinkLabel")}
                        </Link>
                      </Anchor>
                      {t("noTimeWindowGroups4")}
                    </span>
                  </div>
                ) : (
                  <div className="grid gap-[1%] grid-cols-2 xs:grid-cols-3 md:grid-cols-4 xl:grid-cols-7 w-full mb-2">
                    {columns}
                  </div>
                )}
                <Modal
                  opened={selectModalOpened}
                  onClose={closeSelectModal}
                  size={"xl"}
                  fullScreen={isMobile}
                  title={
                    <h2 className="text-xl font-bold">
                      {t("selectTimeWindowGroup")}
                    </h2>
                  }
                >
                  <TimeWindowGroupSelection
                    timeWindowGroupList={timeWindowGroupList}
                    onSelect={onTimeWindowGroupSelect}
                    onCreateNew={onAddTimeWindowGroup}
                    isMobile={isMobile}
                  />
                </Modal>
                <Popup
                  opened={deleteModalOpened}
                  onClose={closeDeleteModal}
                  prepare={undefined}
                  acceptColor="red"
                  titleFactory={() => t("deleteTitle")}
                  onMainAction={deleteCurrent}
                  childrenFactory={() => <div>{t("deleteWarning")}</div>}
                  acceptNode={t("confirmDelete")}
                />
              </>
            )}
          </>
        )}
      </Container>
    </ScrollArea>
  );
};

type TimeWindowShort = Omit<TimeWindow, "dayOfWeek" | "zoneNumber">;

type CellTimeWindow = {
  zoneNumber: number;
  dayOfWeek: number;
  value?: TimeWindowShort;
};

const Cell = memo(
  ({
    zoneNumber: zoneNumber,
    dayOfWeek: dayOfWeek,
    timeWindow,
    onEdit,
  }: {
    zoneNumber: number;
    dayOfWeek: number;
    timeWindow?: TimeWindowShort;
    onEdit: (content: CellTimeWindow) => void;
  }): JSX.Element => {
    const unactivatedTextClass = " text-gray-400";
    const isActive = timeWindow != null;

    return (
      <div
        onBlur={(e) => {
          if (
            !e.currentTarget.contains(e.relatedTarget) &&
            // in case the end value is 00:00 - 00:00
            timeWindow != null &&
            timeWindow.startHour +
              timeWindow.startMinute +
              timeWindow.endHour +
              timeWindow.endMinute ==
              0
          ) {
            onEdit({ zoneNumber: zoneNumber, dayOfWeek: dayOfWeek });
          }
        }}
      >
        <div className="flex flex-row border-[1px] p-2 justify-center items-center">
          <div className="flex-1 flex justify-center">
            <TimePicker
              value={
                timeWindow != null
                  ? {
                      hour: timeWindow.startHour,
                      minute: timeWindow.startMinute,
                    }
                  : undefined
              }
              onChange={(time) => {
                const newTwm: CellTimeWindow = {
                  value: {
                    startHour: time?.hour ?? 0,
                    startMinute: time?.minute ?? 0,
                    endHour: timeWindow?.endHour ?? 0,
                    endMinute: timeWindow?.endMinute ?? 0,
                  },
                  zoneNumber: zoneNumber,
                  dayOfWeek: dayOfWeek,
                };
                onEdit(newTwm);
              }}
            />
          </div>
          <div
            className={
              "mx-[1%]" + (!isActive ? unactivatedTextClass : " font-bold")
            }
          >
            &#8209;
          </div>
          <div className="flex-1 flex justify-center">
            {/* <TimeInput
            ref={endRef}
            value={end}
            onChange={(e) => {
              const time = getTime(e.target.value);
              const newTwm: CellTimeWindow = {
                value: {
                  endHour: time?.hour ?? 0,
                  endMinute: time?.minute ?? 0,
                  startHour: timeWindow?.startHour ?? 0,
                  startMinute: timeWindow?.startMinute ?? 0,
                },
                zoneNumber: zoneNumber,
                dayOfWeek: dayOfWeek,
              };
              onEdit(newTwm);
            }}
            className={inputWrapperClassName}
            unstyled={true}
            classNames={{
              input: inputClassName,
            }}
          /> */}
            <TimePicker
              value={
                timeWindow != null
                  ? { hour: timeWindow.endHour, minute: timeWindow.endMinute }
                  : undefined
              }
              onChange={(time) => {
                const newTwm: CellTimeWindow = {
                  value: {
                    endHour: time.hour,
                    endMinute: time.minute ?? 0,
                    startHour: timeWindow?.startHour ?? 0,
                    startMinute: timeWindow?.startMinute ?? 0,
                  },
                  zoneNumber: zoneNumber,
                  dayOfWeek: dayOfWeek,
                };
                onEdit(newTwm);
              }}
            />
          </div>
        </div>
      </div>
    );
  }
);
Cell.displayName = "Cell";

function toTimeWindowShortArray(timeWindows: TimeWindow[]) {
  const initialArray = Array<TimeWindowShort | undefined>(56).fill(undefined);
  timeWindows.forEach((tw) => {
    initialArray[tw.dayOfWeek * 8 + tw.zoneNumber] = {
      startHour: tw.startHour,
      startMinute: tw.startMinute,
      endHour: tw.endHour,
      endMinute: tw.endMinute,
    };
  });
  return initialArray;
}

function areTimeWindowShortsEqual(
  a: TimeWindowShort | undefined,
  b: TimeWindowShort | undefined
) {
  if (a == b || (a == null && b == null)) {
    return true;
  }
  if (a == null || b == null) {
    return false;
  }
  return (
    a.startHour == b.startHour &&
    a.startMinute == b.startMinute &&
    a.endHour == b.endHour &&
    a.endMinute == b.endMinute
  );
}

export default TimeWindowGroupList;
